import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import ChallengeCard from "@/components/ChallengeCard";
import SolutionCard from "@/components/SolutionCard";
import ExpertCard from "@/components/ExpertCard";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Users, FileText, TrendingDown, Lightbulb, Rocket, Heart, HelpCircle } from "lucide-react";
import { useContentSnippets } from "@/hooks/useContentSnippets";
import { supabase } from "@/integrations/supabase/client";
import { BedarfsAnalysatorDialog } from "@/components/BedarfsAnalysatorDialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

interface Expert {
  id: string;
  name: string;
  title: string;
  quote: string;
  profile_image: string | null;
  display_order: number;
}

const Index = () => {
  const { getSnippet, loading } = useContentSnippets();
  const [experts, setExperts] = useState<Expert[]>([]);
  const [expertsLoading, setExpertsLoading] = useState(true);
  const [showBedarfsAnalysator, setShowBedarfsAnalysator] = useState(false);

  useEffect(() => {
    const fetchExperts = async () => {
      const { data, error } = await supabase
        .from('experts')
        .select('id, name, title, quote, profile_image, display_order')
        .order('display_order', { ascending: true })
        .limit(3);

      if (error) {
        console.error('Error fetching experts:', error);
      } else {
        setExperts(data || []);
      }
      setExpertsLoading(false);
    };

    fetchExperts();
  }, []);

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "MAIP - Management Agiler Innovations-Projekte in der Pflege",
    "url": "https://www.maip.info",
    "logo": "https://www.maip.info/hero-image.jpg",
    "description": "Praxisorientierte KI-Weiterbildung für Führungskräfte und Nachwuchstalente in der Pflege",
    "sameAs": [
      "https://www.linkedin.com/company/maip-info"
    ]
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="MAIP | KI-Weiterbildung für Führungskräfte in der Pflege"
        description="Befähigen Sie sich und Ihr Team für die Zukunft. MAIP ist das praxisorientierte Programm, um KI-Projekte in der ambulanten & stationären Pflege erfolgreich zu managen."
        canonical="https://www.maip.info/"
        schema={organizationSchema}
      />
      <Header />
      
      <Hero />

      {/* 1. Challenges */}
      <section id="challenges" className="py-20 bg-muted">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-muted/50 rounded-2xl p-8 md:p-12">
            <h2 className="text-center text-foreground mb-12">
              {loading ? "Kennen Sie diese Herausforderungen?" : getSnippet("home_challenges_intro", "Kennen Sie diese Herausforderungen?")}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
            <ChallengeCard
              icon={Users}
              title={loading ? "Fachkräftemangel" : getSnippet("home_challenge_1_title", "Fachkräftemangel")}
              description={loading ? "Der demografische Wandel und der Personalmangel setzen Ihre Teams unter enormen Druck." : getSnippet("home_challenge_1_desc", "Der demografische Wandel und der Personalmangel setzen Ihre Teams unter enormen Druck.")}
              iconColor="text-red-500"
            />
            <ChallengeCard
              icon={FileText}
              title={loading ? "Dokumentationslast" : getSnippet("home_challenge_2_title", "Dokumentationslast")}
              description={loading ? "Wertvolle Zeit für den Menschen geht durch wachsende bürokratische Anforderungen verloren." : getSnippet("home_challenge_2_desc", "Wertvolle Zeit für den Menschen geht durch wachsende bürokratische Anforderungen verloren.")}
              iconColor="text-orange-500"
            />
            <ChallengeCard
              icon={TrendingDown}
              title={loading ? "Kostendruck" : getSnippet("home_challenge_3_title", "Kostendruck")}
              description={loading ? "Steigende Kosten erfordern effizientere Prozesse, ohne die Qualität der Versorgung zu gefährden." : getSnippet("home_challenge_3_desc", "Steigende Kosten erfordern effizientere Prozesse, ohne die Qualität der Versorgung zu gefährden.")}
              iconColor="text-yellow-500"
            />
          </div>
          </div>
        </div>
      </section>

      {/* 2. Solutions */}
      <section id="solutions" className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-center text-foreground mb-4">
            {loading ? "Ihr Rüstzeug für die digitale Transformation mit MAIP" : getSnippet("home_toolbox_h2", "Ihr Rüstzeug für die digitale Transformation mit MAIP")}
          </h2>
          <p className="text-center text-muted-foreground mb-16 max-w-3xl mx-auto text-lg">
            {loading
              ? "Ob erfahrene Führungskraft oder ambitionierter Nachwuchs – MAIP gibt Ihnen die Werkzeuge, um Ihre Ideen in erfolgreiche KI-Projekte zu verwandeln und als Innovator sichtbar zu werden."
              : getSnippet(
                  "home_intro_text",
                  "Ob erfahrene Führungskraft oder ambitionierter Nachwuchs – MAIP gibt Ihnen die Werkzeuge, um Ihre Ideen in erfolgreiche KI-Projekte zu verwandeln und als Innovator sichtbar zu werden."
                )}
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <SolutionCard
              icon={Lightbulb}
              title={loading ? "KI-Strategie für die Pflege" : getSnippet("home_toolbox_kachel1_title", "KI-Strategie für die Pflege")}
              description={loading ? "Lernen Sie, die echten Potenziale von KI für Ihre Einrichtung zu identifizieren und zu bewerten." : getSnippet("home_solution_1_desc", "Lernen Sie, die echten Potenziale von KI für Ihre Einrichtung zu identifizieren und zu bewerten.")}
              iconColor="text-primary"
            />
            <SolutionCard
              icon={Rocket}
              title={loading ? "Agiles Projektmanagement" : getSnippet("home_solution_2_title", "Agiles Projektmanagement")}
              description={loading ? "Setzen Sie KI-Projekte mit modernen Methoden schnell, flexibel und erfolgreich um." : getSnippet("home_solution_2_desc", "Setzen Sie KI-Projekte mit modernen Methoden schnell, flexibel und erfolgreich um.")}
              iconColor="text-green-500"
            />
            <SolutionCard
              icon={Heart}
              title={loading ? "Menschenzentriertes Veränderungsmanagement" : getSnippet("home_toolbox_kachel3_title", "Menschenzentriertes Veränderungsmanagement")}
              description={loading ? "Gewinnen Sie Ihre Mitarbeitenden für den Wandel und gestalten Sie die Transformation verantwortungsvoll." : getSnippet("home_solution_3_desc", "Gewinnen Sie Ihre Mitarbeitenden für den Wandel und gestalten Sie die Transformation verantwortungsvoll.")}
              iconColor="text-purple-500"
            />
          </div>
        </div>
      </section>

      {/* 3. Modul 0 (kostenlos) - Blauer Block */}
      <section id="program" className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-gradient-to-r from-primary to-accent"
        >
          <div className="absolute inset-0 bg-black/20"></div>
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <h2 className="mb-8 text-white drop-shadow-lg" dangerouslySetInnerHTML={{ 
            __html: loading ? "Starten Sie hier:<br />Ihr kostenfreier Einstieg in die Welt der KI" : getSnippet("home_modul0_h2", "Starten Sie hier:<br />Ihr kostenfreier Einstieg in die Welt der KI")
          }} />
          <p className="text-xl leading-relaxed mb-10 text-white drop-shadow-md">
            {loading 
              ? "Legen Sie mit unserem kostenlosen Online-Modul \"KI-Grundlagen für Entscheider:innen\" die Wissensgrundlage für Ihre KI-Projekte. 5 interaktive Lektionen vom Leitbild bis zur Roadmap."
              : getSnippet("home_modul0_p", "Legen Sie mit unserem kostenlosen Online-Modul \"KI-Grundlagen für Entscheider:innen\" die Wissensgrundlage für Ihre KI-Projekte. 5 interaktive Lektionen vom Leitbild bis zur Roadmap.")}<br />
            {loading ? "Keine technischen Vorkenntnisse erforderlich – wir vermitteln Management-Kompetenz." : getSnippet("home_modul0_teaser_text", "Keine technischen Vorkenntnisse erforderlich – wir vermitteln Management-Kompetenz.")}
            {" "}{loading ? "Praxisnah, verständlich und direkt anwendbar." : getSnippet("home_modul0_p2", "Praxisnah, verständlich und direkt anwendbar.")}
          </p>
          <Link to="/modul-0">
            <Button 
              size="lg"
              className="bg-white text-primary hover:bg-white/90 px-10 py-6 text-lg font-bold shadow-xl hover:shadow-2xl transition-all"
            >
              {loading ? "Zu den KI-Grundlagen" : getSnippet("home_modul0_cta", "Zu den KI-Grundlagen")}
            </Button>
          </Link>
        </div>
      </section>

      {/* 4. Bedarfs-Analysator CTA */}
      <section className="py-16 px-6 bg-background">
        <div className="max-w-6xl mx-auto">
          <Card className="border border-primary/20 bg-gradient-to-r from-primary/5 to-accent/5 hover:shadow-lg transition-all duration-300 animate-fade-in">
            <CardContent className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                    <HelpCircle className="w-8 h-8 text-primary" />
                  </div>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <Badge variant="secondary" className="mb-2 text-xs">
                    Orientierungshilfe
                  </Badge>
                  <h2 className="text-2xl md:text-3xl font-bold mb-3 text-foreground">
                    Unsicher, welches Modul das richtige für Sie ist?
                  </h2>
                  <p className="text-base text-muted-foreground mb-4">
                    Beantworten Sie 3 kurze Fragen zu Ihrer Rolle und Ihren Zielen. Unser Bedarfs-Analysator empfiehlt Ihnen in 60 Sekunden den perfekten Lernpfad.
                  </p>
                  <Button 
                    size="lg" 
                    onClick={() => setShowBedarfsAnalysator(true)}
                    className="hover-scale"
                  >
                    3-Minuten-Check starten
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* 5. Premium-Module: Datenschutz & Mitbestimmung */}
      <section className="py-16 bg-background">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              {loading ? "Erweitern Sie Ihr Wissen: Unsere Online-Vertiefungsmodule" : getSnippet("home_premium_h2", "Erweitern Sie Ihr Wissen: Unsere Online-Vertiefungsmodule")}
            </h2>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto">
              {loading ? "Praxisorientierte, selbstgesteuerte Lernmodule für spezifische Kompetenzbereiche" : getSnippet("home_premium_p", "Praxisorientierte, selbstgesteuerte Lernmodule für spezifische Kompetenzbereiche")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 animate-fade-in">
            {/* Datenschutz-Kompass */}
            <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl p-6 md:p-8 border border-primary/20 flex flex-col hover:shadow-xl transition-all duration-300 hover-scale">
              <div className="text-center mb-5">
                <div className="inline-block px-3 py-1 bg-primary/20 rounded-full mb-3">
                  <span className="text-xs font-bold text-primary">Premium-Modul</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-2">
                  {loading ? "Datenschutz-Kompass" : getSnippet("home_datenschutz_title", "Datenschutz-Kompass")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {loading ? "Ihr Weg zur DSGVO-Konformität – 10 interaktive Lektionen mit Checklisten, Vorlagen und Zertifikat" : getSnippet("home_datenschutz_desc", "Ihr Weg zur DSGVO-Konformität – 10 interaktive Lektionen mit Checklisten, Vorlagen und Zertifikat")}
                </p>
              </div>
              
              <div className="grid grid-cols-3 gap-3 mb-5 mt-auto">
                <div className="bg-background/80 backdrop-blur rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-primary mb-1">10</div>
                  <div className="text-xs text-muted-foreground">Lektionen</div>
                </div>
                <div className="bg-background/80 backdrop-blur rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-primary mb-1">6-8h</div>
                  <div className="text-xs text-muted-foreground">Lerndauer</div>
                </div>
                <div className="bg-background/80 backdrop-blur rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-primary mb-1">128€</div>
                  <div className="text-xs text-muted-foreground">zzgl. MwSt.</div>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Link to="/datenschutz-kompass-info">
                  <Button size="lg" className="w-full hover-scale">
                    Mehr erfahren & kaufen
                  </Button>
                </Link>
                <Link to="/datenschutz-kompass">
                  <Button size="lg" variant="outline" className="w-full">
                    Kostenlose Lektion testen
                  </Button>
                </Link>
              </div>
            </div>

            {/* KI & Mitbestimmung */}
            <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl p-6 md:p-8 border border-primary/20 flex flex-col hover:shadow-xl transition-all duration-300 hover-scale">
              <div className="text-center mb-5">
                <div className="inline-block px-3 py-1 bg-primary/20 rounded-full mb-3">
                  <span className="text-xs font-bold text-primary uppercase">Neu für MAV & BR</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-2">
                  {loading ? "KI & Mitbestimmung: Vom Blockierer zum Gestalter" : getSnippet("home_mitbestimmung_title", "KI & Mitbestimmung: Vom Blockierer zum Gestalter")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {loading ? "Lernen Sie, wie Sie als MAV konstruktiv KI-Projekte mitgestalten statt blockieren und eine partnerschaftliche Rolle einnehmen." : getSnippet("home_mitbestimmung_desc", "Lernen Sie, wie Sie als MAV konstruktiv KI-Projekte mitgestalten statt blockieren und eine partnerschaftliche Rolle einnehmen.")}
                </p>
              </div>
              
              <div className="grid grid-cols-3 gap-3 mb-5 mt-auto">
                <div className="bg-background/80 backdrop-blur rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-primary mb-1">6</div>
                  <div className="text-xs text-muted-foreground">Lektionen</div>
                </div>
                <div className="bg-background/80 backdrop-blur rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-primary mb-1">6-7h</div>
                  <div className="text-xs text-muted-foreground">Lerndauer</div>
                </div>
                <div className="bg-background/80 backdrop-blur rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-primary mb-1">149€</div>
                  <div className="text-xs text-muted-foreground">zzgl. MwSt.</div>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Link to="/mitbestimmung-info">
                  <Button size="lg" className="w-full hover-scale">
                    Mehr erfahren & buchen
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Potenzial-Analysator Teaser */}
      <section className="py-16 px-6 bg-gradient-to-br from-primary/10 to-accent/10">
        <div className="max-w-6xl mx-auto text-center animate-fade-in">
          <h2 className="text-2xl md:text-3xl font-bold mb-4 text-foreground">
            {loading ? "Entfesseln Sie das Potenzial: Was KI für Ihre Einrichtung leisten kann" : getSnippet("home_potential_h2", "Entfesseln Sie das Potenzial: Was KI für Ihre Einrichtung leisten kann")}
          </h2>
          <p className="text-base text-muted-foreground mb-3 max-w-3xl mx-auto">
            {loading 
              ? "Administrative Last kostet mehr als nur Zeit – sie kostet bares Geld. Berechnen Sie in 90 Sekunden, welches finanzielle Potenzial Sie durch die Optimierung von Dokumentation, Planung und Kommunikation heben können."
              : getSnippet("home_potential_p1", "Administrative Last kostet mehr als nur Zeit – sie kostet bares Geld. Berechnen Sie in 90 Sekunden, welches finanzielle Potenzial Sie durch die Optimierung von Dokumentation, Planung und Kommunikation heben können. Entdecken Sie ungenutzte Potenziale in allen Bereichen Ihrer Organisation.")}
          </p>
          <p className="text-sm text-muted-foreground mb-6 max-w-3xl mx-auto">
            {loading 
              ? <><strong>Konkrete Beispiele:</strong> Den Dokumentationsaufwand erheblich reduzieren, Dienste fairer planen, die Sicherheit Ihrer Bewohnenden erhöhen und mehr Zeit für das Wesentliche gewinnen – den Menschen.</>
              : getSnippet("home_potential_text", "Konkrete Beispiele: Reduzieren Sie den Dokumentationsaufwand erheblich*, planen Sie Dienste fairer, erhöhen Sie die Sicherheit Ihrer Bewohnenden und gewinnen Sie mehr Zeit für das Wesentliche – den Menschen. *Basierend auf Studien, z.B. von Microsoft & Nuance (2023).")}
          </p>
          <Link to="/potenzial-analyse">
            <Button size="lg" className="hover-scale">
              {loading ? "Jetzt Potenzial kostenlos analysieren" : getSnippet("home_potential_cta", "Jetzt Potenzial kostenlos analysieren")}
            </Button>
          </Link>
        </div>
      </section>

      {/* 7. Gestaltungsrat Card */}
      <section className="py-16 px-6 bg-background">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-r from-accent/10 to-primary/10 rounded-xl border border-primary/20 p-6 md:p-8 transition-all duration-300 hover:shadow-lg animate-fade-in">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex-1 text-center md:text-left">
                <div className="inline-block px-3 py-1 bg-primary/20 rounded-full mb-3">
                  <span className="text-primary font-bold text-xs uppercase tracking-wide">
                    {loading ? "Ihre Praxis-Idee ist gefragt!" : getSnippet("home_gestaltungsrat_badge", "Ihre Praxis-Idee ist gefragt!")}
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold mb-3 text-foreground">
                  {loading ? "Gestalten Sie mit: Ihre Impulse für die Pflege von morgen" : getSnippet("home_impuls_h2", "Gestalten Sie mit: Ihre Impulse für die Pflege von morgen")}
                </h2>
                <p className="text-base text-muted-foreground mb-4">
                  {loading 
                    ? "Was ist Ihre größte Herausforderung im Alltag? Ihr Feedback hilft uns, praxisnahe KI-Lösungen zu entwickeln, die wirklich funktionieren. Anonym und in 30 Sekunden."
                    : getSnippet("home_gestaltungsrat_p", "Was ist Ihre größte Herausforderung im Alltag? Ihr Feedback hilft uns, praxisnahe KI-Lösungen zu entwickeln, die wirklich funktionieren. Anonym und in 30 Sekunden.")}
                </p>
                <Link to="/wissen/gestaltungsrat">
                  <Button 
                    size="lg"
                    className="hover-scale"
                  >
                    {loading ? "Impuls teilen" : getSnippet("home_gestaltungsrat_cta", "Impuls teilen")}
                  </Button>
                </Link>
              </div>
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
                  <Users className="w-10 h-10 text-primary" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. Experten */}
      <section id="experts" className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-center text-foreground mb-16">
            {loading ? "Ihre Begleitung auf dem Weg zur KI-Kompetenz" : getSnippet("home_experts_h2", "Ihre Begleitung auf dem Weg zur KI-Kompetenz")}
          </h2>
          {expertsLoading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Lade Experten...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-12">
              {experts.map((expert) => (
                <ExpertCard
                  key={expert.id}
                  image={expert.profile_image || "/placeholder.svg"}
                  name={expert.name}
                  title={expert.title}
                  quote={expert.quote}
                  imageScale={expert.name === 'Ingo Zang' ? 1.68 : 1}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <BedarfsAnalysatorDialog 
        open={showBedarfsAnalysator} 
        onOpenChange={setShowBedarfsAnalysator} 
      />

      <Footer />
    </div>
  );
};

export default Index;
